import React, { useState, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { getMillionInterrogation } from '../utils/gemini';
import { db, auth } from '../firebase'; 
import { doc, updateDoc } from 'firebase/firestore'; // updateDoc for existing user
import { useNavigate } from 'react-router-dom';

const Gatekeeper = () => {
  const navigate = useNavigate();
  const sigCanvas = useRef({});
  const [messages, setMessages] = useState([{ sender: 'ai', text: 'IDENTIFY YOURSELF. Why are you attempting to enter the Family?' }]);
  const [userInput, setUserInput] = useState('');
  const [contractUnlocked, setContractUnlocked] = useState(false);
  const [coreWhy, setCoreWhy] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleSend = async (text) => {
    if (!text) return;
    const newHistory = [...messages, { sender: 'user', text }];
    setMessages(newHistory);
    setUserInput('');
    setProcessing(true);

    const aiResponse = await getMillionInterrogation(messages, text);
    setProcessing(false);

    if (aiResponse.includes("ACCEPTED")) {
      setCoreWhy(text); 
      setContractUnlocked(true);
      setMessages([...newHistory, { sender: 'ai', text: "PASS GRANTED. STEP FORWARD AND SEAL THE CONTRACT." }]);
    } else if (aiResponse.includes("FAILED_PROTOCOL")) {
       navigate('/failed');
    } else {
      setMessages([...newHistory, { sender: 'ai', text: aiResponse }]);
    }
  };

  const handleSealContract = async () => {
      if (!auth.currentUser) return;
      const signatureData = sigCanvas.current.toDataURL();
      
      // Update user doc with contract data
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          contractSigned: true,
          coreWhy: coreWhy,
          signature: signatureData,
          signedAt: new Date().toISOString()
      });
      
      // Navigate to the new Million Wall
      navigate('/million-wall');
  };

  // STYLES (Reused from old ProtocolZero for visual consistency for now)
  const styles = {
    container: { minHeight: '100vh', background: '#000', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', fontFamily: 'Orbitron, sans-serif' },
    headerArea: { textAlign: 'center', marginTop: '40px', borderBottom: '1px solid #D4AF37', paddingBottom: '20px', width: '80%' },
    mainLogo: { letterSpacing: '8px', fontSize: '2.5rem', margin: 0 },
    mobTag: { color: '#D4AF37', fontSize: '1rem' },
    subTag: { color: '#666', fontSize: '0.9rem', letterSpacing: '4px', marginTop: '10px' },
    gateWrapper: { display: 'flex', width: '90%', maxWidth: '1200px', justifyContent: 'space-around', alignItems: 'center', marginTop: '50px', gap: '40px' },
    millionSilhouette: { width: '300px', height: '500px', background: 'linear-gradient(180deg, #111 0%, #000 100%)', borderRadius: '50% 50% 0 0', position: 'relative', border: '1px solid #222', boxShadow: '0 0 50px rgba(212,175,55,0.1)' },
    visorGlow: { position: 'absolute', top: '80px', left: '25%', width: '50%', height: '5px', background: '#D4AF37', boxShadow: '0 0 20px #D4AF37' },
    terminal: { flex: 1, background: '#050505', border: '1px solid #333', borderRadius: '5px', padding: '20px', display: 'flex', flexDirection: 'column', height: '500px' },
    terminalHeader: { fontSize: '0.7rem', color: '#D4AF37', marginBottom: '15px', borderBottom: '1px solid #222', paddingBottom: '5px' },
    chatBox: { flex: 1, overflowY: 'auto', marginBottom: '20px' },
    aiMsg: { color: '#D4AF37', marginBottom: '15px', fontFamily: 'Courier New', fontSize: '1.1rem' },
    userMsg: { color: '#fff', marginBottom: '15px', textAlign: 'right', fontFamily: 'Courier New' },
    senderLabel: { fontWeight: 'bold', fontSize: '0.8rem', color: '#444' },
    loading: { color: '#D4AF37', animation: 'pulse 1s infinite', fontSize: '0.8rem' },
    inputArea: { display: 'flex', gap: '10px' },
    textInput: { flex: 1, background: '#000', border: '1px solid #D4AF37', color: '#fff', padding: '15px', fontFamily: 'Courier New' },
    sendBtn: { background: '#D4AF37', color: '#000', border: 'none', fontWeight: 'bold', padding: '0 20px', cursor: 'pointer' },
    overlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.9)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    contractPaper: { background: '#fff', color: '#000', padding: '40px', width: '90%', maxWidth: '600px', textAlign: 'center' },
    contractTitle: { fontFamily: 'Courier New', borderBottom: '2px solid #000', paddingBottom: '10px' },
    contractText: { fontFamily: 'Courier New', margin: '20px 0', fontSize: '1.2rem' },
    sigBox: { border: '1px dashed #000', margin: '20px 0' },
    finalBtn: { width: '100%', background: '#000', color: '#D4AF37', padding: '20px', border: 'none', fontSize: '1.5rem', fontWeight: 'bold', cursor: 'pointer' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerArea}>
        <h1 style={styles.mainLogo}>MILLION OR BUST <span style={styles.mobTag}>M.O.B.</span></h1>
        <h2 style={styles.subTag}>GATEKEEPER PROTOCOL v3.0</h2>
      </div>
      <div style={styles.gateWrapper}>
        <div style={styles.millionSilhouette}><div style={styles.visorGlow}></div></div>
        <div style={styles.terminal}>
          <div style={styles.terminalHeader}>SECURE CONNECTION: ESTABLISHED</div>
          <div style={styles.chatBox}>
            {messages.map((msg, i) => (
                <div key={i} style={msg.sender === 'ai' ? styles.aiMsg : styles.userMsg}>
                    <span style={styles.senderLabel}>{msg.sender === 'ai' ? '[GATEKEEPER]:' : '[RECRUIT]:'}</span> {msg.text}
                </div>
            ))}
            {processing && <div style={styles.loading}>ANALYZING RESOLVE...</div>}
          </div>
          {!contractUnlocked && (
            <div style={styles.inputArea}>
              <input value={userInput} onChange={e => setUserInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSend(userInput)} placeholder="State your intent..." style={styles.textInput} />
              <button onClick={() => handleSend(userInput)} style={styles.sendBtn}>EXE</button>
            </div>
          )}
        </div>
      </div>
      {contractUnlocked && (
          <div style={styles.overlay}>
            <div style={styles.contractPaper}>
                <h2 style={styles.contractTitle}>BINDING BLOOD CONTRACT</h2>
                <p style={styles.contractText}>I hereby commit 100% to the M.O.B. Protocol. <br/><strong>MISSION:</strong> "{coreWhy}"</p>
                <div style={styles.sigBox}><SignatureCanvas penColor='black' canvasProps={{width: 500, height: 180, className: 'sigCanvas'}} ref={sigCanvas} /></div>
                <button onClick={handleSealContract} style={styles.finalBtn}>SEAL DESTINY</button>
            </div>
          </div>
      )}
    </div>
  );
};

export default Gatekeeper;
